import React, { useState, useEffect } from 'react';
import { payersApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Label } from '../../components/ui/label';
import {
  Plus, PencilSimple, Power, Buildings, Phone, EnvelopeSimple, Clock,
  FunnelSimple, MagnifyingGlass, Database, CaretDown, CaretUp, Globe,
} from '@phosphor-icons/react';
import { Toaster, toast } from 'sonner';

const PAYER_TYPES = [
  { value: 'insurance', label: 'Insurance' },
  { value: 'tpa', label: 'TPA' },
  { value: 'corporate', label: 'Corporate' },
  { value: 'government', label: 'Government' },
];

const TYPE_COLORS = {
  insurance: 'bg-blue-100 text-blue-700',
  tpa: 'bg-violet-100 text-violet-700',
  corporate: 'bg-amber-100 text-amber-700',
  government: 'bg-emerald-100 text-emerald-700',
};

const emptyForm = { name: '', type: 'insurance', category: '', contact_person: '', phone: '', email: '', address: '', settlement_days: 30 };

const PayersPage = () => {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';
  const [payers, setPayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPayer, setEditingPayer] = useState(null);
  const [formData, setFormData] = useState({...emptyForm});
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [scopeFilter, setScopeFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [seeding, setSeeding] = useState(false);
  const [sortField, setSortField] = useState('name');
  const [sortDir, setSortDir] = useState('asc');
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [contactPayer, setContactPayer] = useState(null);
  const [contactForm, setContactForm] = useState({ contact_person: '', phone: '', email: '', address: '', settlement_days: 30, category: '' });

  useEffect(() => { loadPayers(); }, []);

  const loadPayers = async () => {
    try {
      const res = await payersApi.list();
      setPayers(res.data);
    } catch { toast.error('Failed to load payers'); }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name) { toast.error('Payer name is required'); return; }
    try {
      if (editingPayer) {
        await payersApi.update(editingPayer.id, formData);
        toast.success('Payer updated');
      } else {
        await payersApi.create(formData);
        toast.success('Payer created');
      }
      setDialogOpen(false);
      setEditingPayer(null);
      setFormData({...emptyForm});
      loadPayers();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleEdit = (p) => {
    setEditingPayer(p);
    setFormData({ name: p.name, type: p.type, category: p.category || '', contact_person: p.contact_person || '', phone: p.phone || '', email: p.email || '', address: p.address || '', settlement_days: p.settlement_days || 30 });
    setDialogOpen(true);
  };

  const handleToggle = async (p) => {
    try {
      const res = await payersApi.toggle(p.id);
      toast.success(res.data.message);
      loadPayers();
    } catch { toast.error('Failed to toggle payer'); }
  };

  const handleSeedIndia = async () => {
    const msg = isSuperAdmin
      ? 'Load all Indian Insurance, TPA, Corporate, and Government payers as GLOBAL payers for all hospitals? (Existing payers will not be duplicated)'
      : 'Load all Indian Insurance, TPA, Corporate, and Government payers? (Existing payers will not be duplicated)';
    if (!window.confirm(msg)) return;
    setSeeding(true);
    try {
      const res = await payersApi.seedIndia();
      toast.success(res.data.message);
      loadPayers();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to seed'); }
    finally { setSeeding(false); }
  };

  const openContactDialog = (p) => {
    setContactPayer(p);
    setContactForm({
      contact_person: p.contact_person || '',
      phone: p.phone || '',
      email: p.email || '',
      address: p.address || '',
      settlement_days: p.settlement_days || 30,
      category: p.category || '',
    });
    setContactDialogOpen(true);
  };

  const saveContactOverride = async () => {
    try {
      await payersApi.contactOverride(contactPayer.id, contactForm);
      toast.success('Contact details saved for your hospital!');
      setContactDialogOpen(false);
      setContactPayer(null);
      loadPayers();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save contact'); }
  };

  const filtered = payers
    .filter(p => {
      if (statusFilter === 'active' && !p.is_active) return false;
      if (statusFilter === 'inactive' && p.is_active) return false;
      if (typeFilter !== 'all' && p.type !== typeFilter) return false;
      if (!isSuperAdmin && scopeFilter === 'global' && p.tenant_id !== '__global__') return false;
      if (!isSuperAdmin && scopeFilter === 'hospital' && p.tenant_id === '__global__') return false;
      if (search && !p.name.toLowerCase().includes(search.toLowerCase()) && !(p.category || '').toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    })
    .sort((a, b) => {
      const av = (a[sortField] || '').toString().toLowerCase();
      const bv = (b[sortField] || '').toString().toLowerCase();
      return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
    });

  const globalCount = payers.filter(p => p.tenant_id === '__global__').length;
  const hospitalCount = payers.filter(p => p.tenant_id !== '__global__').length;
  const stats = {
    total: payers.length,
    active: payers.filter(p => p.is_active).length,
    inactive: payers.filter(p => !p.is_active).length,
    insurance: payers.filter(p => p.type === 'insurance').length,
    tpa: payers.filter(p => p.type === 'tpa').length,
    corporate: payers.filter(p => p.type === 'corporate').length,
    government: payers.filter(p => p.type === 'government').length,
  };

  const SortHeader = ({ field, children }) => (
    <button className="flex items-center gap-1 text-xs uppercase tracking-wider" onClick={() => { setSortField(field); setSortDir(d => d === 'asc' ? 'desc' : 'asc'); }}>
      {children}
      {sortField === field && (sortDir === 'asc' ? <CaretUp className="h-3 w-3" /> : <CaretDown className="h-3 w-3" />)}
    </button>
  );

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-500"></div></div>;

  return (
    <div className="space-y-6" data-testid="payers-page">
      <Toaster position="top-right" richColors />

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-800 font-['Outfit']" data-testid="payers-title">Payers / TPA</h1>
          <p className="text-sm text-slate-500 mt-1">
            {isSuperAdmin
              ? 'Manage global payers visible to all hospitals'
              : 'Manage insurance companies, TPAs, corporate and government payers'}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="gap-2 border-violet-200 text-violet-600 hover:bg-violet-50"
            onClick={handleSeedIndia} disabled={seeding} data-testid="seed-india-btn">
            <Database className="h-4 w-4" /> {seeding ? 'Loading...' : 'Load India Payers'}
          </Button>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) { setEditingPayer(null); setFormData({...emptyForm}); } }}>
            <DialogTrigger asChild>
              <Button className="bg-gradient-to-r from-sky-500 to-blue-600 text-white shadow-lg shadow-sky-500/25 gap-2" data-testid="add-payer-btn">
                <Plus className="h-4 w-4" /> Add {isSuperAdmin ? 'Global ' : ''}Payer
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle className="font-['Outfit']">{editingPayer ? 'Edit Payer' : 'Add New Payer'}</DialogTitle></DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 pt-2">
                <div><Label>Name *</Label>
                  <Input value={formData.name} onChange={e => setFormData(f => ({...f, name: e.target.value}))} placeholder="Payer name" required data-testid="payer-name-input" /></div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Type *</Label>
                    <Select value={formData.type} onValueChange={v => setFormData(f => ({...f, type: v}))}>
                      <SelectTrigger data-testid="payer-type-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{PAYER_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                    </Select></div>
                  <div><Label>Category</Label>
                    <Input value={formData.category} onChange={e => setFormData(f => ({...f, category: e.target.value}))} placeholder="e.g. Private, PSU, TPA" /></div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Contact Person</Label><Input value={formData.contact_person} onChange={e => setFormData(f => ({...f, contact_person: e.target.value}))} /></div>
                  <div><Label>Settlement Days</Label><Input type="number" value={formData.settlement_days} onChange={e => setFormData(f => ({...f, settlement_days: parseInt(e.target.value) || 30}))} /></div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Phone</Label><Input value={formData.phone} onChange={e => setFormData(f => ({...f, phone: e.target.value}))} /></div>
                  <div><Label>Email</Label><Input type="email" value={formData.email} onChange={e => setFormData(f => ({...f, email: e.target.value}))} /></div>
                </div>
                <div><Label>Address</Label><Input value={formData.address} onChange={e => setFormData(f => ({...f, address: e.target.value}))} /></div>
                <Button type="submit" className="w-full bg-gradient-to-r from-sky-500 to-blue-600 text-white" data-testid="save-payer-btn">
                  {editingPayer ? 'Update Payer' : 'Add Payer'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-7 gap-3">
        {[
          { label: 'Total', value: stats.total, color: 'text-slate-800' },
          { label: 'Active', value: stats.active, color: 'text-emerald-700' },
          { label: 'Inactive', value: stats.inactive, color: 'text-red-600' },
          { label: 'Insurance', value: stats.insurance, color: 'text-blue-700' },
          { label: 'TPA', value: stats.tpa, color: 'text-violet-700' },
          { label: 'Corporate', value: stats.corporate, color: 'text-amber-700' },
          { label: 'Govt', value: stats.government, color: 'text-emerald-700' },
        ].map(s => (
          <Card key={s.label} className="bg-white border border-slate-200 shadow-sm"><CardContent className="pt-3 pb-3 text-center">
            <p className="text-[10px] text-slate-500 font-medium uppercase tracking-wider">{s.label}</p>
            <p className={`text-xl font-bold ${s.color}`}>{s.value}</p>
          </CardContent></Card>
        ))}
      </div>

      {/* Filters */}
      <div className="flex gap-3 items-center flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input placeholder="Search payers..." value={search} onChange={e => setSearch(e.target.value)}
            className="pl-9 border-slate-300" data-testid="search-payers" />
        </div>
        <FunnelSimple className="h-4 w-4 text-slate-400" />
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-32 border-slate-300"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
          </SelectContent>
        </Select>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-36 border-slate-300"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            {PAYER_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
          </SelectContent>
        </Select>
        {!isSuperAdmin && (
          <Select value={scopeFilter} onValueChange={setScopeFilter}>
            <SelectTrigger className="w-36 border-slate-300" data-testid="scope-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Sources</SelectItem>
              <SelectItem value="global">Global ({globalCount})</SelectItem>
              <SelectItem value="hospital">Hospital ({hospitalCount})</SelectItem>
            </SelectContent>
          </Select>
        )}
        <span className="text-xs text-slate-400">{filtered.length} result{filtered.length !== 1 ? 's' : ''}</span>
      </div>

      {/* Table */}
      <Card className="bg-white border border-slate-200 shadow-sm" data-testid="payers-table">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="text-slate-500"><SortHeader field="name">Payer Name</SortHeader></TableHead>
                  <TableHead className="text-slate-500"><SortHeader field="type">Type</SortHeader></TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Category</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Contact</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-center">Settlement</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-center"><SortHeader field="is_active">Status</SortHeader></TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map(p => (
                  <TableRow key={p.id} className={`hover:bg-sky-50/50 transition-colors ${!p.is_active ? 'opacity-50' : ''}`} data-testid={`payer-row-${p.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className={`h-7 w-7 rounded-md flex items-center justify-center text-white text-[10px] font-bold ${p.is_active ? 'bg-gradient-to-br from-sky-400 to-violet-400' : 'bg-slate-300'}`}>
                          {p.name?.charAt(0)?.toUpperCase()}
                        </div>
                        <div className="flex flex-col">
                          <span className="font-medium text-slate-800 text-sm">{p.name}</span>
                          {p.tenant_id === '__global__' && !isSuperAdmin && (
                            <span className="flex items-center gap-0.5 text-[9px] text-indigo-500 font-medium">
                              <Globe className="h-2.5 w-2.5" /> Global
                            </span>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell><Badge className={`${TYPE_COLORS[p.type] || 'bg-slate-100 text-slate-600'} border-0 text-[10px]`}>
                      {PAYER_TYPES.find(t => t.value === p.type)?.label || p.type}</Badge></TableCell>
                    <TableCell className="text-xs text-slate-500">{p.category || '-'}</TableCell>
                    <TableCell>
                      {p.contact_person || p.phone || p.email ? (
                        <div className="space-y-0.5">
                          {p.contact_person && <p className="text-xs text-slate-600">{p.contact_person}</p>}
                          {p.phone && <div className="flex items-center gap-1 text-[10px] text-slate-400"><Phone className="h-3 w-3" />{p.phone}</div>}
                          {p.email && <div className="flex items-center gap-1 text-[10px] text-slate-400"><EnvelopeSimple className="h-3 w-3" />{p.email}</div>}
                        </div>
                      ) : <span className="text-xs text-slate-300">-</span>}
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex items-center justify-center gap-1 text-xs text-slate-500"><Clock className="h-3 w-3 text-slate-400" />{p.settlement_days}d</div>
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge className={`border-0 text-[10px] ${p.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                        {p.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-0.5">
                        {(isSuperAdmin || p.tenant_id !== '__global__') ? (
                          <>
                            <Button variant="ghost" size="sm" className="hover:bg-sky-50 text-sky-600 h-7 w-7 p-0" onClick={() => handleEdit(p)} data-testid={`edit-payer-${p.id}`}>
                              <PencilSimple className="h-3.5 w-3.5" /></Button>
                            <Button variant="ghost" size="sm" className={`h-7 w-7 p-0 ${p.is_active ? 'hover:bg-red-50 text-red-500' : 'hover:bg-emerald-50 text-emerald-600'}`}
                              onClick={() => handleToggle(p)} data-testid={`toggle-payer-${p.id}`} title={p.is_active ? 'Deactivate' : 'Activate'}>
                              <Power className="h-3.5 w-3.5" /></Button>
                          </>
                        ) : (
                          <Button variant="ghost" size="sm" className="hover:bg-violet-50 text-violet-600 h-7 px-2 gap-1 text-[10px]"
                            onClick={() => openContactDialog(p)} data-testid={`edit-contact-${p.id}`} title="Edit contact for your hospital">
                            <PencilSimple className="h-3 w-3" /> Contact
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {filtered.length === 0 && (
                  <TableRow><TableCell colSpan={7} className="text-center py-12 text-slate-500">
                    <Buildings className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                    <p className="text-sm">{payers.length === 0 ? 'No payers yet. Click "Load India Payers" to get started.' : 'No payers match the filters'}</p>
                  </TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Contact Override Dialog for Global Payers */}
      <Dialog open={contactDialogOpen} onOpenChange={(open) => { setContactDialogOpen(open); if (!open) setContactPayer(null); }}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Outfit']">Edit Payer Details</DialogTitle>
          </DialogHeader>
          {contactPayer && (
            <div className="space-y-4">
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                <p className="text-sm font-semibold text-slate-800">{contactPayer.name}</p>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-[10px] text-slate-400">Type: {contactPayer.type}</span>
                  <span className="text-[10px] text-slate-400">|</span>
                  <span className="text-[10px] text-violet-500">Your hospital's details are saved separately</span>
                </div>
              </div>
              <div><Label>Category</Label>
                <Input value={contactForm.category} onChange={e => setContactForm(f => ({...f, category: e.target.value}))}
                  placeholder="e.g. Private, PSU, TPA" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Contact Person</Label>
                  <Input value={contactForm.contact_person} onChange={e => setContactForm(f => ({...f, contact_person: e.target.value}))}
                    placeholder="Name" data-testid="contact-person-input" /></div>
                <div><Label>Settlement Days</Label>
                  <Input type="number" value={contactForm.settlement_days} onChange={e => setContactForm(f => ({...f, settlement_days: parseInt(e.target.value) || 0}))}
                    placeholder="30" /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Phone</Label>
                  <Input value={contactForm.phone} onChange={e => setContactForm(f => ({...f, phone: e.target.value}))}
                    placeholder="Phone number" data-testid="contact-phone-input" /></div>
                <div><Label>Email</Label>
                  <Input type="email" value={contactForm.email} onChange={e => setContactForm(f => ({...f, email: e.target.value}))}
                    placeholder="Email address" data-testid="contact-email-input" /></div>
              </div>
              <div><Label>Address</Label>
                <Input value={contactForm.address} onChange={e => setContactForm(f => ({...f, address: e.target.value}))}
                  placeholder="Office address" /></div>
              <Button className="w-full bg-gradient-to-r from-sky-500 to-blue-600 text-white" onClick={saveContactOverride} data-testid="save-contact-btn">
                Save Details
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PayersPage;
